import type { EventFile, EventSummary } from "@/lib/types/eventList";
import type { Testimonials } from "@/lib/types/testimonial";
import { eventFormat, type EventFormat } from "@/components/explore/eventCardModel";
import type { HomeCopy } from "@/components/home/copy";

/**
 * A small card's event (UpcomingStrip: an event page's Outros eventos), trimmed on
 * the server from the list answer (montanhas-web#16): the page is cached between
 * visitors, so a card carries nothing per client, and no availability either --
 * the label before a title is filled in after mount, from the list the browser
 * asks for itself.
 */
export type HomeEvent = Pick<EventSummary, "id" | "programme_id" | "title" | "start_time" | "end_time" | "type" | "difficulty" | "price" | "price_child" | "late_price" | "registration_state"> & {
  /** the event's own banner, else its programme's */
  banner: EventFile | null;
  /** the banner first, then the first few photos of the gallery (the user's call: the banner leads) */
  photos: EventFile[];
};

/** what the page says about the events to come, worked out on the server once */
export type HomeSummary = {
  /** how many events are to come */
  count: number;
  /** when the first of them starts */
  first: string | null;
  /** the lowest adult price of each format, for the shortcuts' "desde X €"; null with none of that format */
  cheapest: Record<EventFormat, number | null>;
};

/**
 * An event of Início's Próximos eventos (montanhas-web#21), drawn as Explore's card
 * (EventCard) with its photos to swipe: the list's event as nobody gets it, less
 * its text, its gallery and what changes by the minute (availability, Novidade),
 * which come after mount with the browser's own list, as the labels do.
 */
export type UpcomingEvent = EventSummary & {
  /** the banner first, then the first few photos of the gallery (the user's call: the banner leads) */
  slides: EventFile[];
};

export type HomeData = {
  upcoming: UpcomingEvent[];
  summary: HomeSummary;
  /** the blocks' words, from the CMS snippets over the defaults (copy.ts) */
  copy: HomeCopy;
  /** O que dizem de nós: the testimonials staff featured and every event's rating (montanhas-web#19) */
  testimonials: Testimonials | null;
};

// how many photos a card offers to swipe after its banner
export const CARD_PHOTOS = 4;
// how many events Próximos eventos shows, before Ver todos (the user's call, montanhas-web#21)
export const UPCOMING_SIZE = 6;

const trimFile = (file: EventFile): EventFile => ({ id: file.id, path: file.path, thumbnail: file.thumbnail ?? null, width: file.width ?? null, height: file.height ?? null });

/** an event as the page needs it, and nothing more: the banner, then `photos` of the gallery */
export const homeEvent = (event: EventSummary, photos = 0): HomeEvent => {
  const banner = (event.banners || [])[0] || null;
  const gallery = (event.photos || []).slice(0, photos).map(trimFile);
  return {
    id: event.id,
    programme_id: event.programme_id,
    title: event.title,
    start_time: event.start_time,
    end_time: event.end_time,
    type: event.type,
    difficulty: event.difficulty,
    price: event.price,
    price_child: event.price_child,
    late_price: event.late_price,
    registration_state: event.registration_state,
    banner: banner ? trimFile(banner) : null,
    photos: [...(banner ? [trimFile(banner)] : []), ...gallery],
  };
};

export const upcomingEvent = (event: EventSummary): UpcomingEvent => {
  const banner = (event.banners || [])[0] || null;
  return {
    ...event,
    description: null,
    short_description: null,
    photos: [],
    banners: banner ? [trimFile(banner)] : [],
    availability: null,
    places_left: null,
    is_new: false,
    slides: homeEvent(event, CARD_PHOTOS).photos,
  };
};

export const homeSummary = (events: EventSummary[]): HomeSummary => {
  const cheapest: Record<EventFormat, number | null> = { SINGLE_DAY: null, WEEKEND: null, TRIP: null };
  events.forEach((event) => {
    const price = Number(event.price);
    if (!(price > 0)) return;
    const format = eventFormat(event);
    cheapest[format] = cheapest[format] === null ? price : Math.min(cheapest[format]!, price);
  });
  return { count: events.length, first: events[0]?.start_time || null, cheapest };
};

/** the image a card leads with: the banner, the poster it is, else the first photo of the gallery */
export const leadPhoto = (event: HomeEvent) => event.banner || event.photos[0] || null;

/**
 * The proportions a card's image box takes: the banner's own, so it shows whole
 * and uncropped (they are drawn at 16:9, but not all exactly), else 16:9. A CSS
 * aspect-ratio value.
 */
export const bannerRatio = (event: HomeEvent) => (event.banner && event.banner.width && event.banner.height ? `${event.banner.width} / ${event.banner.height}` : "16 / 9");
